import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { QueryClient, QueryClientProvider } from "react-query";
import { useSelector } from "react-redux";

import {
  userIsAuthenticated,
  userIsAuthenticatedRedir,
  userIsNotAuthenticatedRedir,
} from "./common/auth";
import SuspenseFallbackComponent from "./common/components/SuspenseFallbackComponent";
import ScrollToTop from "./common/ScrollToTop";
import usePlatformInvite from "./common/usePlatformInvite";
import useUTM from "./common/useUTM";
import VerifyEmail from "./common/VerifyEmail";
import ActivityRouteListener from "./common/ActivityRouteListener";
import StartEmailConversationFromRoute from "./gig/chat/components/StartEmailConversationFromRoute";
const GmailAuthSuccessPage = React.lazy(
  () => import("./gig/chat/components/GmailAuthSuccessPage")
);
const GmailAuthErrorPage = React.lazy(
  () => import("./gig/chat/components/GmailAuthErrorPage")
);
import SkyliftSkeleton from "./gig/detail/SkyliftSkeleton";
import OAuthRegisterModal from "./register/OAuthRegisterModal";
import useGeoIp from "./common/useGeoIp";
import useExchangeRate from "./common/useExchangeRate";
import Footer from "./static-pages/common/Footer";
import { useHiddenChatBubble } from "./common/useHiddenChatBubble";
import { useLoginTokenToRenew } from "./common/useLoginTokenToRenew";
import "./App.css";
import { PushNotificationProvider } from "./contexts/PushNotificationContext";
import AskForShortListModal from "./login/AskForShortListModal";
import useAuthWall from "./common/useAuthWall";
import { initializePWAInstallPrompt } from "./common/pwaHelpers";
import useScreenOrientation from "./common/hooks/useScreenOrientation";

const ApplyFormContainer = React.lazy(
  () =>
    import("./user-dashboard/components/ProgramsDashBoard/ApplyFormContainer")
);

const UserPlannerContainer = React.lazy(
  () => import("./user-dashboard/UserPlannerContainer")
);

const DestinationComparisonContainer = React.lazy(
  () => import("./comparison/DestinationComparisonContainer")
);
const Scholarships = React.lazy(
  () => import("./scholarships/ScholarshipsContainer")
);

const OnBoardingContainer = React.lazy(
  () => import("./user-dashboard/components/OnBoardingContainer")
);
const UserDashboardContainer = React.lazy(
  () => import("./user-dashboard/UserDashboardContainer")
);
const ProgramFinder = React.lazy(() => import("./programs/ProgramContainer"));
const EiRocContainer = React.lazy(() => import("./ei-roc/EiRocContainer"));
const CompSciContainer = React.lazy(
  () => import("./summit/comp-sci-360/CompSciContainer")
);
const AllOrdersContainer = React.lazy(
  () => import("./orders/AllOrdersContainer")
);
const OrderContainer = React.lazy(() => import("./orders/OrderContainer"));
const ApplicantSetupContainer = React.lazy(
  () => import("./job-listings/applicants/setup/ApplicantSetupContainer")
);
const CreateOrUpdateJobContainer = React.lazy(
  () => import("./job-listings/jobs/CreateOrUpdateJobContainer")
);
const JobDetailsContainer = React.lazy(
  () => import("./job-listings/jobs/details/JobDetailsContainer")
);
const JobSearchContainer = React.lazy(
  () => import("./job-listings/jobSearch/JobSearchContainer")
);
const ApplicantListingContainer = React.lazy(
  () => import("./job-listings/applicants/listing/ApplicantListingContainer")
);

const ApplicantDashboardContainer = React.lazy(
  () => import("./job-listings/ApplicantDashboard/ApplicantDashboardContainer")
);

const CompanySetup = React.lazy(() => import("./job-listings/company/setup"));

const GigFeedbackContainer = React.lazy(
  () => import("./gig/gig-feedback/GigFeedbackContainer")
);

const LayoutGrid = React.lazy(
  () => import("./common/components/layouts/LayoutGrid")
);

const Navbar = React.lazy(() => import("./navbar/NavbarContainer"));

const QuestContainer = React.lazy(() => import("./quest/QuestContainer"));

const GigDetailContainer = React.lazy(
  () => import("./gig/detail/DetailContainer")
);

const ListingContainer = React.lazy(
  () => import("./gig/listing/ListingContainer")
);

const LoginModal = React.lazy(() => import("./login/LoginModal"));

const GigListingCategoryContainer = React.lazy(
  () => import("./gig/listing-category/GigListingCategoryContainer")
);

const CommentModal = React.lazy(
  () => import("./comment/components/CommentModal")
);

const ChatEmbed = userIsAuthenticatedRedir(
  React.lazy(() => import("./gig/chat/embed/ChatEmbedContainer"))
);

const UserPresenceEmbedContainer = userIsAuthenticatedRedir(
  React.lazy(() => import("./gig/chat/embed/UserPresenceEmbedContainer"))
);

const PollsEmbed = userIsAuthenticatedRedir(
  React.lazy(() => import("./gig/chat/embed/PollsEmbedContainer"))
);

const Home = React.lazy(() => import("./home/HomeContainer"));

const Login = userIsNotAuthenticatedRedir(
  React.lazy(() => import("./login/LoginContainer"))
);
const Register = userIsNotAuthenticatedRedir(
  React.lazy(() => import("./register/RegisterContainer"))
);

const ServiceProvider = userIsAuthenticatedRedir(
  React.lazy(
    () => import("./onboard/service-provider/ServiceProviderContainer")
  )
);

const AwaitingVerificationContainer = userIsAuthenticatedRedir(
  React.lazy(() => import("./common/components/AwaitingVerificationContainer"))
);

const CommunityDetailsContainer = React.lazy(
  () => import("./community/details/DetailsContainer")
);

const ContactUs = React.lazy(
  () => import("./static-pages/contact-us/ContactUs")
);

const CommunityGuideline = React.lazy(
  () => import("./static-pages/community-guidelines/CommunityGuidelines")
);

const NbyulaTalkTerms = React.lazy(
  () => import("./static-pages/terms/NbyulaTalkTerms")
);

const PrivacyPolicy = React.lazy(
  () => import("./static-pages/privacy-policy/PrivacyPolicy")
);
const PrivacyPolicyPCT = React.lazy(
  () => import("./static-pages/privacy-policy/PrivacyPolicyPCT")
);
const Terms = React.lazy(
  () => import("./static-pages/terms/TermsAndCondition")
);
const TermsPCT = React.lazy(
  () => import("./static-pages/terms/TermsAndConditionPCT")
);

const CancellationPolicy = React.lazy(
  () => import("./static-pages/cancellation-policy/CancellationPolicy")
);

const ShippingDeliveryPolicy = React.lazy(
  () => import("./static-pages/shipping-delivery-policy/ShippingDeliveryPolicy")
);

const RefundReturnPolicy = React.lazy(
  () => import("./static-pages/refund-return-policy/RefundReturnPolicy")
);

const Meet = userIsAuthenticatedRedir(
  React.lazy(() => import("./meet/MeetContainer"))
);

const GigDetailContainerAuthenticated = userIsAuthenticatedRedir(
  React.lazy(() => import("./gig/detail/DetailContainer"))
);
const ProfileContainer = React.lazy(() => import("./profile/ProfileContainer"));

const ProfileGamePlayerIdToUserNameDecorator = React.lazy(
  () => import("./profile/ProfileGamePlayerIdToUserNameDecorator")
);

const WhoAmIContainer = React.lazy(() => import("./whoami/WhoAmIContainer"));

const ProtectedProfileContainer = userIsAuthenticatedRedir(
  React.lazy(() => import("./profile/ProfileContainer"))
);
const UnSubscribeNotificationContainer = React.lazy(
  () => import("./notifications/UnSubscribe/Un-subscribeNotificationContainer")
);

const PricingPolicyComponent = React.lazy(
  () => import("./static-pages/pricing-policy/PricingPolicy")
);

const StartAQuest = React.lazy(
  () => import("./quest/feed/StartAQuest/StartAQuestContainer")
);

const AboutUsComponent = React.lazy(
  () => import("./static-pages/about-us/AboutUs")
);
const CareersContainer = React.lazy(
  () => import("./static-pages/careers/CareersContainer")
);
const EnterprisePage = React.lazy(
  () => import("./static-pages/enterprise/EnterprisePage")
);

const AnabinContainer = React.lazy(
  () => import("./utility-apps/anabin/AnabinContainer")
);

const AnabinInstituteContainer = userIsAuthenticatedRedir(
  React.lazy(() => import("./utility-apps/anabin/institute/InstituteContainer"))
);

const Coupons = userIsAuthenticatedRedir(
  React.lazy(() => import("./payments/components/Coupons"))
);

const EctsCalculatorContainer = React.lazy(
  () => import("./utility-apps/ects-calculator/EctsCalculatorContainer")
);

const GpaCalculator = React.lazy(
  () => import("./utility-apps/gpa-calculator/GpaCalculatorContainer")
);

const JobContainer = React.lazy(() => import("./careers/JobContainer"));

const Search = React.lazy(() => import("./search"));

const CreateOrUpdateModal = React.lazy(
  () => import("./assessments/components/CreateOrUpdateModal")
);

const Settings = React.lazy(() => import("./settings"));

const VerifyContainer = React.lazy(() => import("./verify/VerifyContainer"));

const SOPBuilderLandingPage = React.lazy(() => import("./sop/SopContainer"));
// Protected dashboard-only entry point (`/s/sop-builder/home`). Unlike the
// public landing route, unauthenticated visitors get the login/sign-up modal
// (via `userIsAuthenticated`) instead of being shown the marketing page.
const SOPBuilderDashboard = userIsAuthenticated(SOPBuilderLandingPage);
const SOPEditorContainer = userIsAuthenticatedRedir(
  React.lazy(() => import("./sop/editor/EditorContainer"))
);

const StaticSOPLandingPage = React.lazy(
  () => import("./sop/StaticSOPLandingPageContainer")
);

const EssayLandingPage = React.lazy(
  () => import("./sop/EssayLandingPageContainer")
);

const LorLandingPage = React.lazy(
  () => import("./lor/LorLandingPageContainer")
);
const LorDashboard = userIsAuthenticatedRedir(
  React.lazy(() => import("./lor/LorContainer"))
);
// Single LOR builder route. The container itself picks the owner builder vs the
// internal MOD/ADMIN "expert" view based on who opens which letter — there is no
// separate expert URL anymore.
const LorEditorContainer = userIsAuthenticatedRedir(
  React.lazy(() => import("./lor/editor/LorEditorContainer"))
);
// Public (NOT auth-guarded): referees authenticate via a virtual-login token in
// the URL, so this route must not redirect account-less visitors to login.
const LorRefereeContainer = React.lazy(
  () => import("./lor/editor/LorRefereeContainer")
);

const Consent = React.lazy(
  () => import("./static-pages/terms/ApplicationProcessingPartnerConsent")
);
const PlannerAnalyticsContainer = React.lazy(
  () =>
    import("./user-dashboard/analytics/planner-analytics/PlannerAnalyticsContainer")
);

const Accommodation = React.lazy(
  () => import("./accommodation/AccommodationComponent")
);

const PricingPage = React.lazy(() => import("./pricing/PricingContainer"));

const CartContainer = React.lazy(() => import("./cart/CartContainer"));

const SubscriptionContainer = React.lazy(() => import("./subscription/SubscriptionContainer"));

const TestimonialsContainer = React.lazy(
  () => import("./testimonials/TestimonialsContainer")
);

const VirtualOffice = React.lazy(
  () => import("./virtual-office/VirtualOfficeContainer")
);

const MarangoniVirtualOffice = React.lazy(
  () => import("./virtual-office/marangoni/MarangoniVirtualOfficeContainer")
);

const OutboundCallsContainer = React.lazy(
  () => import("./ai-outbound-calls/OutboundCallsContainer")
);

const LoanVirtualOffice = React.lazy(
  () => import("./virtual-office/loans/LoanVirtualOfficeContainer")
);

const AccommodationVirtualOffice = React.lazy(
  () =>
    import(
      "./virtual-office/accommodation/AccommodationVirtualOfficeContainer"
    )
);

const InvestorWallContainer = React.lazy(
  () => import("./investor-wall/InvestorWallContainer")
);

const ProtectedOpsDashboard = React.lazy(
  () => import("./ops-dashboard/ProtectedOpsDashboard")
);

const ProtectedLayoutGrid = userIsAuthenticatedRedir(LayoutGrid);

function App({ history }) {
  const isStartAQuestActive = useSelector((state) =>
    Boolean(state.quests?.isStartAQuestActive)
  );

  const loggedInUserId = useSelector((state) => state.user?.profile?._id);

  const isAssessmentWorkflowActive = useSelector((state) =>
    Boolean(state.assessment.manage?.isActive)
  );

  const showCommentModal = useSelector((state) => state.comments?.showModal);

  const oAuthRegisterModal = useSelector(
    (state) => state.user.oAuthRegisterModal
  );

  const jobApplyModal = useSelector((state) =>
    Boolean(state.jobListings.isApplyModalActive)
  );

  const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
  });

  const showToast = (title, body, link, icon) => {
    toast(
      <CustomChatNotification
        title={title}
        body={body}
        link={link}
        avatarSrc={icon}
      />,
      {
        toastId: link,
        autoClose: 5000,
        position: "top-center",
        className: "message-notification__wrapper",
        bodyClassName: "message-notification__body",
      }
    );
  };

  const openNotification = (title, body, icon, link) => {
    if (window.location === window.parent.location)
      showToast(title, body, link, icon);
  };

  // Firebase messaging logic moved to PushNotificationProvider

  // Initialize PWA install prompt listener
  useEffect(() => {
    initializePWAInstallPrompt();
  }, []);

  usePlatformInvite();
  useUTM();
  useGeoIp();
  useExchangeRate();
  useLoginTokenToRenew();
  useHiddenChatBubble();
  useAuthWall();
  useScreenOrientation();

  return (
    <QueryClientProvider client={queryClient}>
      <PushNotificationProvider loggedInUserId={loggedInUserId}>
        <Router history={history}>
          {oAuthRegisterModal && <OAuthRegisterModal />}
          {jobApplyModal && (
            <Suspense fallback={<SuspenseFallbackComponent />}>
              <ApplicantSetupContainer />
            </Suspense>
          )}

          <VerifyEmail />
          <ScrollToTop />
          <ActivityRouteListener />

          <Suspense fallback={<SuspenseFallbackComponent />}>
            <LoginModal />
          </Suspense>

          <Suspense fallback={<SuspenseFallbackComponent />}>
            <AskForShortListModal />
          </Suspense>

          {isStartAQuestActive && (
            <Suspense fallback={<SuspenseFallbackComponent />}>
              <StartAQuest />
            </Suspense>
          )}

          {isAssessmentWorkflowActive && (
            <Suspense fallback={<SuspenseFallbackComponent />}>
              <CreateOrUpdateModal />
            </Suspense>
          )}

          {showCommentModal && (
            <Suspense fallback={<SuspenseFallbackComponent />}>
              <CommentModal />
            </Suspense>
          )}

          <Suspense fallback={<SuspenseFallbackComponent />}>
            <Switch>
              <Route
                path="/search"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LayoutGrid showFooter {...props} mainFeed={<Search />} />
                    </Suspense>
                  </>
                )}
              />

              <Route
                path="/profile/:username"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ProfileContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/virtual-office"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <VirtualOffice {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/istituto-marangoni-virtual-office"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <MarangoniVirtualOffice {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/admin/ai-outbound-calls"}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <OutboundCallsContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/ops-dashboard"}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ProtectedOpsDashboard {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/loan-eligibility-check"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LoanVirtualOffice {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/accommodation-eligibility-check"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <AccommodationVirtualOffice {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/live-product-data-room"
                exact
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <InvestorWallContainer {...props} />
                  </Suspense>
                )}
              />
              <Route
                path={"/study-abroad-planner/analytics"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <PlannerAnalyticsContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={[
                  "/shortlist/:userName",
                  "/shortlist",
                  "/planner/:app/:userName",
                  "/planner/:app",
                  "/planner",
                ]}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <div className="h-full w-full bg-secondary-light-600">
                        <LayoutGrid
                          {...props}
                          mainFeed={<UserPlannerContainer />}
                        />
                      </div>
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={["/study-abroad-planner"]}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <OnBoardingContainer />
                    </Suspense>
                  </>
                )}
              />

              <Route
                path={["/program/application/:applicationId"]}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ApplyFormContainer />
                    </Suspense>
                  </>
                )}
              />

              <Route
                path="/coupons"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LayoutGrid
                        mainFeed={<Coupons {...props} />}
                        showFooter={true}
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/profile/id/:gamePlayerId"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ProfileGamePlayerIdToUserNameDecorator {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/profile/:username/:intent/:attribute?/:metadata?"
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ProtectedProfileContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={`/quest/:questId/(intent)?/:intention?`}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <div className="flex flex-col">
                        <LayoutGrid
                          showFooter
                          {...props}
                          mainFeed={<QuestContainer />}
                        />
                      </div>
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={`/q/:slug/(intent)?/:intention?`}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <div className="flex flex-col">
                        <LayoutGrid
                          showFooter
                          {...props}
                          mainFeed={<QuestContainer />}
                        />
                      </div>
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/c/:slug"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <CommunityDetailsContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/gigs/listings"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ListingContainer className="mt-8" />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/gigs/listings/:username/:slug"
                exact
                render={(props) => (
                  <Suspense fallback={<SkyliftSkeleton showNav />}>
                    <Navbar />
                    <LayoutGrid
                      {...props}
                      showFooter
                      footerWrapperClassName="mb-10 lg:mb-0"
                      mainFeed={<GigDetailContainer {...props} />}
                    />
                  </Suspense>
                )}
              />
              <Route
                path="/gigs/listings/:username/:slug/:intent/:metadata?"
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LayoutGrid
                        {...props}
                        mainFeed={
                          <GigDetailContainerAuthenticated {...props} />
                        }
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/gigs/template/:templateId"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LayoutGrid
                        {...props}
                        showFooter
                        mainFeed={<GigDetailContainer {...props} />}
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/gigs/template/:templateId/:intent/:metadata?"
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LayoutGrid
                        {...props}
                        mainFeed={
                          <GigDetailContainerAuthenticated {...props} />
                        }
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/gigs/category/:categoryName"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LayoutGrid
                        {...props}
                        mainFeed={
                          <GigListingCategoryContainer className="mt-8" />
                        }
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/gigs/feedback/listings/:username/:slug"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LayoutGrid
                        {...props}
                        mainFeed={<GigFeedbackContainer />}
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/embed/meet/presence/:slug/:conversationId"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <LayoutGrid
                      {...props}
                      mainFeed={<UserPresenceEmbedContainer {...props} />}
                      useDefaultLayout={false}
                    />
                  </Suspense>
                )}
              />
              <Route
                path="/embed/conversations/:openConversationId?"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <LayoutGrid
                      {...props}
                      mainFeed={<ChatEmbed {...props} />}
                      useDefaultLayout={false}
                    />
                  </Suspense>
                )}
              />
              <Route
                path="/embed/polls/:conversationId/:eventId"
                render={(props) => (
                  <Suspense
                    fallback={
                      <SuspenseFallbackComponent scaleClass="scale-50" />
                    }
                  >
                    <LayoutGrid
                      {...props}
                      mainFeed={<PollsEmbed {...props} />}
                      useDefaultLayout={false}
                    />
                  </Suspense>
                )}
              />
              <Route
                path="/conversation.create/email/:email/:subject"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <ProtectedLayoutGrid
                      {...props}
                      mainFeed={<StartEmailConversationFromRoute {...props} />}
                      useDefaultLayout={false}
                    />
                  </Suspense>
                )}
              />
              <Route
                path="/comments/:commentId"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <LayoutGrid
                      {...props}
                      mainFeed={<CommentModal {...props} redirectToEntity />}
                      useDefaultLayout
                    />
                  </Suspense>
                )}
              />
              <Route
                path="/brand/setup"
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ProtectedLayoutGrid
                        {...props}
                        mainFeed={<CompanySetup {...props} />}
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                exact
                path={["/jobs", "/jobs/search"]}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <JobSearchContainer />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/s/resume-evaluator"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ApplicantDashboardContainer />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={["/jobs/:action/:jobId?"]}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <CreateOrUpdateJobContainer {...props} />
                      <Footer />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/organization/job/:jobId"
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LayoutGrid
                        {...props}
                        showFooter
                        mainFeed={<JobDetailsContainer {...props} />}
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={[
                  "/recruiter/view-applicants?jobId=jobId",
                  "/recruiter/view-applicants",
                ]}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ProtectedLayoutGrid
                        {...props}
                        mainFeed={<ApplicantListingContainer />}
                      />
                    </Suspense>
                  </>
                )}
              />
              {/* <Route
            path={"/onboarding"}
            render={(props) => (
              <Suspense fallback={<SuspenseFallbackComponent />}>
                <Onboarding {...props} />
              </Suspense>
            )}
          /> */}
              <Route
                path={"/become-a-nbyula-guide"}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ServiceProvider {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/login"}
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <Login {...props} />
                  </Suspense>
                )}
              />
              <Route
                path={"/register"}
                exact
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <Register {...props} />
                  </Suspense>
                )}
              />
              <Route
                path={"/contact-us"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ContactUs {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/privacy-policy-pct"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <PrivacyPolicyPCT {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/privacy-policy"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <PrivacyPolicy {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/terms-pct"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <TermsPCT {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/terms"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <Terms {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/cancellation-policy"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <CancellationPolicy {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/shipping-delivery-policy"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ShippingDeliveryPolicy {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/refund-return-policy"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <RefundReturnPolicy {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/pricing-policy"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <PricingPolicyComponent {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/about-us"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <AboutUsComponent {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/careers"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <CareersContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/enterprise"}
                exact
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <EnterprisePage {...props} />
                  </Suspense>
                )}
              />
              <Route
                path={"/job/:jobId"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <JobContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/community-guidelines"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <CommunityGuideline {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/nbyula-talk-terms"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <NbyulaTalkTerms {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/verify-email"}
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <AwaitingVerificationContainer {...props} />
                  </Suspense>
                )}
              />
              <Route
                exact
                path="/gmail-auth-success"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <GmailAuthSuccessPage {...props} />
                  </Suspense>
                )}
              />
              <Route
                exact
                path="/gmail-auth-error"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <GmailAuthErrorPage {...props} />
                  </Suspense>
                )}
              />
              <Route
                path={"/meet/:slug?"}
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <Meet {...props} />
                  </Suspense>
                )}
              />
              <Route
                path={"/whoami/:userId?"}
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <WhoAmIContainer {...props} />
                  </Suspense>
                )}
              />
              <Route
                path="/notifications/un-subscribe"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <UnSubscribeNotificationContainer {...props} />
                  </>
                )}
              />
              <Route
                path={"/s/ects-calculator"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <EctsCalculatorContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/s/germany-grade-calculator"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <GpaCalculator {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/s/pride"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <AnabinContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/s/pride/search"
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <AnabinInstituteContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/settings"
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ProtectedLayoutGrid {...props} mainFeed={<Settings />} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/verify"
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LayoutGrid
                        showFooter
                        {...props}
                        mainFeed={<VerifyContainer />}
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/order-requests/:orderType?"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <AllOrdersContainer {...props} />
                  </Suspense>
                )}
              />
              <Route
                path="/order/:orderSlug/:university/:program?"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <OrderContainer {...props} />
                  </Suspense>
                )}
              />
              <Route
                path="/compsci360/(thankyou)?"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <CompSciContainer {...props} />
                  </Suspense>
                )}
              />
              <Route
                path="/eiroc"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <EiRocContainer />
                  </Suspense>
                )}
              />
              <Route
                path={["/find-a-program/:userName", "/find-a-program"]}
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ProtectedLayoutGrid
                        {...props}
                        mainFeed={<ProgramFinder />}
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/find-scholarships"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <ProtectedLayoutGrid
                        {...props}
                        mainFeed={<Scholarships />}
                      />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path="/compare/destination"
                exact
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <DestinationComparisonContainer />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={["/s/sop-builder/editor/:sopId", "/s/sop-builder/editor"]}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <SOPEditorContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/s/sop-builder/home"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <SOPBuilderDashboard {...props} forceDashboard />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={[
                  "/s/statement-of-purpose",
                  "/s/sop-builder",
                  "/_ssr/sop-builder",
                ]}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <SOPBuilderLandingPage />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/sop-review"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <StaticSOPLandingPage />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/essay"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <EssayLandingPage />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/s/lor-builder/referee/:token"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LorRefereeContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={[
                  "/s/lor-builder/editor/:lorId",
                  "/s/lor-builder/editor",
                ]}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LorEditorContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/s/lor-builder"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LorDashboard {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={["/lor", "/lor-builder"]}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <LorLandingPage />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/consent/application-filing"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <Navbar />

                      <LayoutGrid {...props} mainFeed={<Consent />} />
                    </Suspense>
                  </>
                )}
              />

              <Route
                path={"/accommodation"}
                render={(props) => (
                  <>
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <Navbar />
                      <LayoutGrid {...props} mainFeed={<Accommodation />} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/pricing"}
                exact
                render={(props) => {
                  // Check if flow=flow1 query parameter is present
                  const urlParams = new URLSearchParams(props.location.search);
                  const shouldHideNavbar = urlParams.get("flow") === "flow1";

                  return (
                    <>
                      {!shouldHideNavbar && <Navbar />}
                      <Suspense fallback={<SuspenseFallbackComponent />}>
                        <PricingPage {...props} />
                      </Suspense>
                    </>
                  );
                }}
              />
              <Route
                path={"/cart"}
                exact
                render={(props) => (
                  <>
                    {/* Show minimal navbar with only logo, notifications, conversations, and profile */}
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <CartContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/subscriptions"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <SubscriptionContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={"/testimonials"}
                exact
                render={(props) => (
                  <>
                    <Navbar />
                    <Suspense fallback={<SuspenseFallbackComponent />}>
                      <TestimonialsContainer {...props} />
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={["/null", "/undefined", "/null/", "/undefined/"]}
                render={(props) => (
                  <Redirect to={`/${props.location.search}`} />
                )}
              />
              <Route
                path="*"
                render={(props) => (
                  <Suspense fallback={<SuspenseFallbackComponent />}>
                    <Home {...props} />
                  </Suspense>
                )}
              />
            </Switch>
            <ToastContainer
              {...{
                hideProgressBar: true,
                position: "bottom-left",
                autoClose: 3000,
                closeButton: true,
                closeOnClick: true,
                enableMultiContainer: true,
                icon: true,
                limit: 4,
                theme: "dark",
              }}
            />
          </Suspense>
        </Router>
      </PushNotificationProvider>
    </QueryClientProvider>
  );
}

export default App;
